import { useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { ScanEye, CheckCircle2, XCircle } from "lucide-react";

export default function BlurReveal() {
  const [data, setData] = useState(null);
  const [guess, setGuess] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await fz.blur.daily();
      setData(d);
      if (d.answer) setAnswer(d.answer);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading…</div>;
  if (!data.available) return <div className="titans-card p-6 text-titans-silver text-sm">Blur Reveal is unavailable right now.</div>;

  const wrong = (data.wrong || []).length;
  const blurPx = data.solved || data.completed ? 0 : Math.max(2, 22 - wrong * 4);

  const submit = async (e) => {
    e.preventDefault();
    if (!guess.trim() || busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.blur.guess(guess.trim());
      setData((d) => ({ ...d, tries_left: r.tries_left, wrong: r.wrong, solved: r.correct, completed: r.completed }));
      if (r.answer) setAnswer(r.answer);
      if (r.correct) toast.success("That's him!");
      else toast.error(r.completed ? "Out of guesses!" : "Nope — image gets clearer…");
      if (r.award) toastAward(r.award);
      setGuess("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Guess failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="titans-card p-5 md:p-6" data-testid="fz-blur">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ScanEye size={16} className="text-titans-blue" />
          <span className="font-display text-xl uppercase">Blur Reveal</span>
        </div>
        <div className="flex gap-1" data-testid="fz-blur-pips">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < wrong ? "bg-titans-red" : "bg-[#E2E8F0]"}`} />
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 items-center">
        <div className="player-img-wrap rounded-xl overflow-hidden h-64 flex items-center justify-center">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${data.image}`}
            alt="Mystery Titan"
            data-testid="fz-blur-image"
            className="relative z-[1] max-h-full object-contain transition-all duration-700"
            style={{ filter: `blur(${blurPx}px)` }}
          />
        </div>
        <div>
          <p className="text-titans-silver text-sm font-body mb-3">
            Guess the current Titan. Every miss sharpens the photo — 6 tries.
          </p>
          {!data.completed ? (
            <form onSubmit={submit} className="space-y-2">
              <input
                list="fz-blur-names"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Type a player name…"
                data-testid="fz-blur-input"
                className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body text-sm"
              />
              <datalist id="fz-blur-names">
                {(data.names || []).map((n) => <option key={n} value={n} />)}
              </datalist>
              <button type="submit" disabled={busy || !guess.trim()} data-testid="fz-blur-submit" className="bg-titans-red hover:bg-titans-red-hover text-white px-5 py-2.5 font-cond uppercase tracking-wider text-xs disabled:opacity-50 w-full sm:w-auto">
                Lock it in
              </button>
            </form>
          ) : (
            <div className={`border-l-4 p-3 ${data.solved ? "border-emerald-500 bg-emerald-500/10" : "border-titans-red bg-titans-red/10"}`} data-testid="fz-blur-result">
              <div className="flex items-center gap-2">
                {data.solved ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-titans-red" />}
                <span className="font-display text-xl uppercase">{answer?.name}</span>
              </div>
              {answer && (
                <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1">
                  {answer.position} {answer.jersey ? `· #${answer.jersey}` : ""} — new mystery player tomorrow
                </div>
              )}
            </div>
          )}
          {(data.wrong || []).length > 0 && !data.completed && (
            <div className="mt-3 font-cond uppercase tracking-wider text-[10px] text-titans-muted">
              Missed: {(data.wrong || []).join(" · ")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
